import { createEffect, type JSX } from "solid-js";
import { X } from "@fleet/icons";
import { cn } from "@aula/shared";

import { useNavDrawerSwipe } from "@/features/layout/hooks/use-nav-drawer-swipe";

/**
 * Panel transform. While open and at rest returns exactly
 * `translate3d(0, 0, 0)`: dragging only appears in style when there is
 * dragging, keeping rest state a uniform, comparable value.
 */
function transformDelPanel(abierto: boolean, arrastrePx: number): string {
  if (!abierto) {
    return "translate3d(-100%, 0, 0)";
  }
  return arrastrePx === 0 ? "translate3d(0, 0, 0)" : `translate3d(${arrastrePx}px, 0, 0)`;
}

/**
 * Mobile navigation drawer, using the same mechanism as the canonical
 * (`space-academy-app` → `features/layout/components/mobile-nav-drawer.tsx`).
 *
 * Key architectural detail is that **it is always mounted** and slides in/out
 * via `transform`, rather than mounting/unmounting (`<Show when={mobileOpen()}>`
 * bare, as before). A node removed from the DOM cannot animate its exit:
 * it vanishes in the exact frame the condition changes. By keeping it mounted,
 * closing is visible — and swipe-to-close works (cannot drag what doesn't exist).
 *
 * Only `transform` (drawer) and `opacity` (backdrop) are animated, the two
 * properties compositor handles without layout recalculation or repaint.
 *
 * Lives separately from shell for two reasons: exact canonical separation,
 * and allows testing without standing up session, router, i18n, and PowerSync.
 */
export function MobileNavDrawer(props: {
  isOpen: () => boolean;
  setOpen: (open: boolean) => void;
  /** Accessible label for close button (already translated). */
  closeLabel: string;
  children: JSX.Element;
}) {
  let drawerContainer: HTMLDivElement | undefined;
  let panel: HTMLElement | undefined;
  // `inert` is set manually as an ATTRIBUTE, not a prop. Solid would assign it
  // as element property, not reflected in DOM: guard would work in browser but
  // remain invisible to tests — green without evidence, avoiding fake checks.
  createEffect(() => {
    drawerContainer?.toggleAttribute("inert", !props.isOpen());
  });

  // Swiping left closes it; swiping right, nothing (see hook).
  // Gesture listens on BOX, not panel, so it also works
  // dragging from dimmed backdrop.
  const swipe = useNavDrawerSwipe({
    isOpen: () => props.isOpen(),
    close: () => {
      props.setOpen(false);
    },
    panel: () => panel,
  });

  return (
    <div
      ref={(el) => {
        drawerContainer = el;
        swipe.attach(el);
      }}
      data-testid="mobile-nav-drawer"
      class={cn(
        // `touch-pan-y`: browser keeps vertical scroll and yields horizontal
        // axis. Without this it competes with gesture and dragging stutters.
        "fixed inset-0 z-40 overflow-hidden touch-pan-y md:hidden transition-all duration-300 ease-swift",
        props.isOpen() ? "pointer-events-auto" : "pointer-events-none",
      )}
      // By staying mounted, its navigation would stay in tab order
      // and accessibility tree when closed: invisible to sighted users
      // but reachable via keyboard and announced by screen readers.
      // `pointer-events-none` only stops pointers, not focus. `inert` does.
    >
      <button
        type="button"
        data-testid="mobile-nav-backdrop"
        onClick={() => props.setOpen(false)}
        class={cn(
          "absolute inset-0 h-full w-full cursor-default border-none bg-black/40 p-0 backdrop-blur-[2px] ease-swift",
          // While the finger leads, the backdrop follows it: the transition is
          // turns off and opacity is fixed by drag.
          swipe.isDragging() ? "transition-none" : "transition-opacity duration-300",
          props.isOpen() ? "opacity-100" : "opacity-0",
        )}
        style={{ opacity: swipe.isDragging() ? String(swipe.backdropOpacity()) : undefined }}
        aria-label={props.closeLabel}
      />
      <aside
        ref={panel}
        data-testid="mobile-nav-aside"
        class={cn(
          "absolute bottom-0 left-0 top-0 h-full w-64 border-r border-border bg-background shadow-2xl ease-swift will-change-transform",
          // With transition, panel would lag 300 ms behind touch position.
          swipe.isDragging() ? "transition-none" : "transition-transform duration-300",
        )}
        style={{
          transform: transformDelPanel(props.isOpen(), swipe.dragPx()),
        }}
      >
        <button
          type="button"
          class="absolute right-2 top-2 text-muted-foreground"
          onClick={() => props.setOpen(false)}
          aria-label={props.closeLabel}
        >
          <X class="h-5 w-5" />
        </button>
        {props.children}
      </aside>
    </div>
  );
}
